import { CloseIcon } from '@/assets/icons'
import { loadErrorMessage } from '@lib/apiError'
import { iconSizes } from '@/theme/tokens'
import ConfirmationModal from '@components/ConfirmationModal'
import { ErrorNote } from '@components/Notice'
import { PageHeaderRow, PageScroll } from '@components/Page'
import PageHeading from '@components/PageHeading'
import PageLoader from '@components/PageLoader'
import { useEvents } from '@hooks/useEvents'
import { useTrip } from '@hooks/useTrip'
import { useUpdateTripDates } from '@hooks/useUpdateTripDates'
import { useUpdateTripDestinations } from '@hooks/useUpdateTripDestinations'
import { HeaderCloseButton } from '@pages/Trips/CreateTripFlow/CreateTripPage.styles'
import FlowShell from '@pages/Trips/CreateTripFlow/FlowShell'
import WhenStep from '@pages/Trips/CreateTripFlow/WhenStep'
import WhereStep from '@pages/Trips/CreateTripFlow/WhereStep'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	EDIT_STEPS,
	EditTripStep,
	editBottomBar,
	editStepIndicatorState,
	editStepLabel,
	removeRefusalMessage,
	removedEventsMessage,
	rowEventsLabel,
	saveErrorMessage,
	strandedEventsMessage,
} from './core/editPresentation'
import {
	EditTripDestinationsState,
	destinationEvents,
	hasChanges,
	saveRequest,
	seedEditState,
	uncoveredDays,
	updateDraft,
} from './core/editState'
import { EditTripDestinationsLocationState } from './paths'
import { TestIds } from '@/testIds'

/**
 * The edit counterpart of the create-trip flow, like iOS: the same Where and
 * When pages over a trip's saved destinations and start date, with Save
 * mapping the edited draft onto the destinations contract (ADR 0007).
 * Replaces the old plain "Edit Trip Dates" modal. Lives at
 * `/trips/:tripId/destinations` under the standard page header, whose back
 * arrow and close button both ask before discarding edits; on save or back it returns to the itinerary,
 * or wherever the entry point said to. The trip's events ride along so each
 * row can say what it holds; removing a row that still holds events is
 * refused on the spot with a toast, and a trim that strands events is called
 * out on the row before Save. The notice above the list stays as the backstop
 * for events that land on a removed row's days while the editor is open.
 */
const EditTripDestinationsPage: React.FC = () => {
	const { tripId } = useParams<{ tripId: string }>()
	const navigate = useNavigate()
	const location = useLocation()
	const { data: trip, isLoading, error } = useTrip(tripId)
	const { data: eventsData, isLoading: eventsLoading } = useEvents(tripId)
	const updateDestinations = useUpdateTripDestinations(tripId ?? '')
	const updateTripDates = useUpdateTripDates(tripId ?? '')

	const [state, setState] = useState<EditTripDestinationsState | null>(null)
	const [step, setStep] = useState<EditTripStep>('where')
	const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false)

	// Seed once, from the first trip that lands — later refetches (the save's
	// own, a realtime nudge) must not reset what the user is editing.
	useEffect(() => {
		if (trip) setState(current => current ?? seedEditState(trip))
	}, [trip])

	const entry = location.state as EditTripDestinationsLocationState | null
	const returnTo = entry?.returnTo ?? `/trips/${tripId}/itinerary`
	const leave = () => navigate(returnTo)

	if (error) {
		return <ErrorNote>{loadErrorMessage('trip', error)}</ErrorNote>
	}
	// The rows count their events, so the editor waits for both.
	if (isLoading || eventsLoading || (trip && !state)) {
		return <PageLoader label="Loading trip…" />
	}
	if (!trip || !state) {
		return <ErrorNote>Trip not found</ErrorNote>
	}

	const events = destinationEvents(state, eventsData?.events ?? [])
	const rowDetails = Object.fromEntries(
		state.draft.destinations.map(row => {
			const { eventCount, strandedCount } = events.rows[row.key]
			return [
				row.key,
				{
					meta: rowEventsLabel(eventCount),
					error: strandedCount > 0 ? strandedEventsMessage(strandedCount) : undefined,
					removeRefusal: removeRefusalMessage(row.place.name, eventCount) ?? undefined,
				},
			]
		}),
	)

	const isSaving = updateDestinations.isPending || updateTripDates.isPending

	// The back arrow is a plain link until there is something to lose; then it asks first.
	const handleBack = (event: React.MouseEvent) => {
		if (!hasChanges(state)) return
		event.preventDefault()
		setIsConfirmingDiscard(true)
	}
	// The close button leaves the same way, for whoever reaches for the flow's ✕.
	const handleClose = () => {
		if (hasChanges(state)) setIsConfirmingDiscard(true)
		else leave()
	}
	const returnLabel = entry?.returnTo ? (entry.returnLabel ?? trip.name) : 'Itinerary'

	const save = async () => {
		const request = saveRequest(state)
		if (!request) return
		try {
			if (request.kind === 'destinations') {
				await updateDestinations.mutateAsync(request.body)
				toast.success('Destinations updated')
			} else {
				await updateTripDates.mutateAsync(request.body)
				toast.success('Trip dates updated')
			}
			leave()
		} catch (saveError) {
			toast.error(saveErrorMessage(saveError, request), { duration: 10000 })
		}
	}

	const handleCta = () => {
		if (step === 'where') {
			setStep('when')
			return
		}
		void save()
	}

	const onDraftChange = (draft: EditTripDestinationsState['draft']) =>
		setState(updateDraft(state, draft))

	return (
		<PageScroll>
			<FlowShell
				header={
					// The standard heading row, built by hand: the close button is a glyph beside the
					// title, not a page action that takes its own row on a phone.
					<PageHeaderRow>
						<PageHeading
							title="Edit Destinations"
							backTo={{ path: returnTo, label: returnLabel, onClick: handleBack }}
						/>
						<HeaderCloseButton type="button" aria-label="Cancel" onClick={handleClose}>
							<CloseIcon sx={{ fontSize: iconSizes.lg }} />
						</HeaderCloseButton>
					</PageHeaderRow>
				}
				onBack={() => setStep('where')}
				steps={EDIT_STEPS.map(candidate => ({
					key: candidate,
					label: editStepLabel(candidate),
					state: editStepIndicatorState(state, candidate, step),
				}))}
				onSelectStep={setStep}
				bar={editBottomBar(state, step, events)}
				ctaPending={isSaving}
				ctaTestId={TestIds.tripDestinations.stepCta(step)}
				onCta={handleCta}
			>
				{step === 'where' && (
					<WhereStep
						draft={state.draft}
						onChange={onDraftChange}
						rowDetails={rowDetails}
						listNotice={removedEventsMessage(events.removed)}
						onRemoveRefused={message => toast.error(message)}
					/>
				)}
				{step === 'when' && (
					<WhenStep
						draft={state.draft}
						onChange={onDraftChange}
						uncoveredDays={uncoveredDays(state)}
					/>
				)}
			</FlowShell>

			<ConfirmationModal
				isOpen={isConfirmingDiscard}
				onClose={() => setIsConfirmingDiscard(false)}
				onConfirm={leave}
				title="Discard your changes?"
				message="Your edits to this trip's places and dates won't be saved."
				confirmText="Discard"
			/>
		</PageScroll>
	)
}

export default EditTripDestinationsPage
